import React from 'react';

export default function CreateCohortWizard({
  wizardState,
  wizardSearchText,
  filteredLeftStudents,
  selectedStudents,
  highlightedStudentId,
  schools,
  spocs,
  counselors,
  auditors,
  handleCancelCohortWizard,
  handleWizardSchoolSelect,
  setWizardSearchText,
  setHighlightedStudentId,
  handleAddStudent,
  handleRemoveStudent,
  setWizardState,
  handleWizardToggleCounselor,
  handleWizardToggleAuditorSelect,
  handleWizardToggleAuditorStandby,
  handleCreateCohortSubmit,
  isStudentAssignedToAnyCohort,
  getAssignedCohortName,
}) {
  return (
    <div className="wizard-card animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">Initialize Student Cohort</h1>
          <p className="dashboard-subtitle">Step {wizardState.step} of 3 — Set up school and group credentials.</p>
        </div>
        <button className="btn-card-cancel" onClick={handleCancelCohortWizard}>Cancel</button>
      </div>

      <div className="wizard-steps-bar">
        <div className={`wizard-step-node ${wizardState.step >= 1 ? 'active' : ''}`}>1</div>
        <div className={`wizard-step-node ${wizardState.step >= 2 ? (wizardState.step === 2 ? 'active' : 'completed') : ''}`}>2</div>
        <div className={`wizard-step-node ${wizardState.step === 3 ? 'active' : ''}`}>3</div>
      </div>

      {wizardState.step === 1 && (
        <div className="animate-fade-in">
          <div className="wizard-form-group">
            <label htmlFor="programmeYear">Programme Year</label>
            <select
              id="programmeYear"
              className="wizard-select"
              value={wizardState.levelYear}
              onChange={(e) => setWizardState(prev => ({ ...prev, levelYear: e.target.value }))}
              required
            >
              <option value="Year 1">Year 1 - Glosco Councils</option>
              <option value="Year 2">Year 2 - Glosco Policy Studio</option>
            </select>
          </div>
          <div className="wizard-form-group">
            <label>Select Institutional Partner School</label>
            <select
              className="wizard-select"
              value={wizardState.schoolId}
              onChange={(e) => handleWizardSchoolSelect(e.target.value)}
            >
              <option value="" disabled>-- Choose Partner School --</option>
              {schools.map(s => (
                <option key={s.id} value={s.id}>{s.school_name || s.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {wizardState.step === 2 && (
        <div className="animate-fade-in">
          <div className="transfer-list-container">
            <div className="transfer-panel">
              <div className="transfer-panel-header">
                <span className="transfer-panel-title">School Roster</span>
              </div>
              <div style={{ padding: '12px' }}>
                <input
                  type="text"
                  className="wizard-search-input"
                  style={{ marginBottom: '12px', width: '100%' }}
                  placeholder="🔍 Search school roster by name or grade..."
                  value={wizardSearchText}
                  onChange={(e) => setWizardSearchText(e.target.value)}
                />
                <div className="transfer-scrollbox">
                  {filteredLeftStudents.length > 0 ? (
                    filteredLeftStudents.map(student => {
                      const isAssigned = isStudentAssignedToAnyCohort(student.id);
                      const assignedCohortName = isAssigned ? getAssignedCohortName(student.id) : null;
                      const isHighlighted = highlightedStudentId === student.id;
                      return (
                        <div
                          key={student.id}
                          className={`transfer-row-item ${isAssigned ? 'disabled' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                          onClick={() => {
                            if (isAssigned) return;
                            setHighlightedStudentId(student.id);
                          }}
                          onDoubleClick={() => {
                            if (isAssigned) return;
                            handleAddStudent(student.id);
                          }}
                        >
                          <div className="transfer-row-main">
                            <span className="transfer-row-label">{student.name}</span>
                            <span className="transfer-row-badge">{student.grade}</span>
                          </div>
                          {isAssigned && (
                            <span className="transfer-row-status-text">
                              Already in {assignedCohortName}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="transfer-empty-state">
                      No students found matching filters.
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-transfer-action"
                  style={{ marginTop: '12px', width: '100%' }}
                  disabled={!highlightedStudentId}
                  onClick={() => {
                    if (highlightedStudentId) {
                      handleAddStudent(highlightedStudentId);
                    }
                  }}
                >
                  Add Selected Student ➔
                </button>
              </div>
            </div>

            <div className="transfer-panel">
              <div className="transfer-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="transfer-panel-title">This Cohort</span>
                <span
                  className="checklist-row-badge"
                  style={{
                    background: wizardState.studentIds.length >= 15 ? '#f89f09' : 'var(--primary-navy)',
                    color: '#ffffff',
                    fontWeight: '600',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  {wizardState.studentIds.length} {wizardState.studentIds.length === 1 ? 'Student' : 'Students'}
                </span>
              </div>
              <div style={{ padding: '12px' }}>
                <div className="transfer-scrollbox" style={{ minHeight: '298px' }}>
                  {selectedStudents.length > 0 ? (
                    selectedStudents.map(student => (
                      <div
                        key={student.id}
                        className="transfer-row-item"
                        onDoubleClick={() => handleRemoveStudent(student.id)}
                      >
                        <div className="transfer-row-main">
                          <span className="transfer-row-label">{student.name}</span>
                          <span className="transfer-row-badge">{student.grade}</span>
                        </div>
                        <button
                          type="button"
                          className="btn-transfer-row-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveStudent(student.id);
                          }}
                          title="Remove from cohort"
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="transfer-empty-state" style={{ minHeight: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      No students added to this cohort yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="wizard-footer-buttons">
            <button className="btn-wizard-back" onClick={() => {
              setWizardState(prev => ({ ...prev, step: 1 }));
              setHighlightedStudentId(null);
            }}>
              Back
            </button>
            <button
              className="btn-wizard-next"
              disabled={wizardState.studentIds.length === 0}
              onClick={() => setWizardState(prev => ({ ...prev, step: 3 }))}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {wizardState.step === 3 && (
        <div className="animate-fade-in">
          <div className="wizard-form-group">
            <label htmlFor="cohortName">Cohort Designation Title</label>
            <input
              id="cohortName"
              type="text"
              className="wizard-select"
              placeholder="e.g. Munich Glosco Cohort Alpha"
              value={wizardState.name}
              onChange={(e) => setWizardState(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div className="wizard-form-group">
            <label htmlFor="spocAssign">Assigned Single Point of Contact (SPOC)</label>
            <select
              id="spocAssign"
              className="wizard-select"
              value={wizardState.spocId}
              onChange={(e) => setWizardState(prev => ({ ...prev, spocId: e.target.value }))}
            >
              <option value="" disabled>-- Choose SPOC (Teacher) --</option>
              {spocs.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
              ))}
            </select>
          </div>

          <div className="wizard-form-group">
            <label>Assign Counselors</label>
            <div className="wizard-checklist-scrollbox" style={{ maxHeight: '120px' }}>
              {counselors.map(c => (
                <div
                  key={c.id}
                  className="checklist-row-item"
                  onClick={() => handleWizardToggleCounselor(c.id)}
                >
                  <input
                    type="checkbox"
                    checked={wizardState.counselorIds.includes(c.id)}
                    onChange={() => {}}
                  />
                  <span className="checklist-row-label">{c.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="wizard-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ marginBottom: 0 }}>Assign Auditors Pool & Set Standby Roles</label>
              {wizardState.auditorAssignments.length > 0 && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: '999px',
                  background: 'rgba(1, 71, 162, 0.1)',
                  color: 'var(--primary-navy)'
                }}>
                  {wizardState.auditorAssignments.length} auditor{wizardState.auditorAssignments.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="auditor-wizard-grid">
              {auditors.map(aud => {
                const assignment = wizardState.auditorAssignments.find(a => a.auditorId === aud.id);
                const isSelected = !!assignment;
                return (
                  <div key={aud.id} className={`auditor-wizard-row ${isSelected ? 'selected' : ''}`}>
                    <div className="auditor-wizard-left" onClick={() => handleWizardToggleAuditorSelect(aud.id)}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                      />
                      <span className="auditor-wizard-name">{aud.name}</span>
                    </div>
                    {isSelected && (
                      <div className="auditor-wizard-right">
                        <div className="standby-toggle-container">
                          <span>STANDBY</span>
                          <label className="standby-switch">
                            <input
                              type="checkbox"
                              checked={assignment.isStandby}
                              onChange={() => handleWizardToggleAuditorStandby(aud.id)}
                            />
                            <span className="standby-slider"></span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="wizard-footer-buttons">
            <button className="btn-wizard-back" onClick={() => setWizardState(prev => ({ ...prev, step: 2 }))}>Back</button>
            <button
              className="btn-wizard-next"
              disabled={!wizardState.name || !wizardState.spocId || wizardState.auditorAssignments.length === 0}
              onClick={handleCreateCohortSubmit}
            >
              Initialize Cohort
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
